import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, XCircle, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

interface ConnectionStatus {
  configured: boolean;
  connected: boolean;
  authenticated: boolean;
  tablesExist: boolean;
  rlsEnabled: boolean;
  error?: string;
  details: {
    url: string;
    hasAnonKey: boolean;
    userCount?: number;
    projectCount?: number;
    lastCheck: string;
  };
}

export const SupabaseConnectionChecker: React.FC = () => {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setIsChecking(true);
    
    const connectionStatus: ConnectionStatus = {
      configured: false,
      connected: false,
      authenticated: false,
      tablesExist: false,
      rlsEnabled: false,
      details: {
        url: import.meta.env.VITE_SUPABASE_URL || 'Non configuré',
        hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
        lastCheck: new Date().toLocaleTimeString('fr-FR')
      }
    };

    try {
      // 1. Check if Supabase is configured
      connectionStatus.configured = isSupabaseConfigured();
      
      if (!connectionStatus.configured) {
        connectionStatus.error = 'Variables d\'environnement Supabase manquantes';
        setStatus(connectionStatus);
        setIsChecking(false);
        return;
      }

      // 2. Test basic connection
      try {
        const { data, error } = await supabase.from('users').select('count', { count: 'exact', head: true });
        
        if (error) {
          connectionStatus.error = `Erreur de connexion: ${error.message}`;
        } else {
          connectionStatus.connected = true;
          connectionStatus.tablesExist = true;
          connectionStatus.details.userCount = data?.length || 0;
        }
      } catch (err: any) {
        connectionStatus.error = `Impossible de se connecter: ${err.message}`;
      }

      // 3. Check authentication
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        connectionStatus.authenticated = !!user && !authError;
      } catch (err) {
        connectionStatus.authenticated = false;
      }

      // 4. Check if projects table exists and get count
      if (connectionStatus.connected) {
        try {
          const { count, error } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true });
          
          if (!error) {
            connectionStatus.details.projectCount = count || 0;
          }
        } catch (err) {
          console.warn('Could not check projects table:', err);
        }
      }

      // 5. Check RLS policies
      if (connectionStatus.connected) {
        try {
          // Try to query a protected table to test RLS
          const { error: rlsError } = await supabase
            .from('users')
            .select('id')
            .limit(1);
          
          connectionStatus.rlsEnabled = !rlsError || !rlsError.message.includes('permission denied');
        } catch (err) {
          connectionStatus.rlsEnabled = false;
        }
      }

    } catch (err: any) {
      connectionStatus.error = `Erreur générale: ${err.message}`;
    }

    setStatus(connectionStatus);
    setIsChecking(false);
  };

  const getStatusIcon = (isOk: boolean, isWarning = false) => {
    if (isOk) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (isWarning) return <AlertTriangle className="w-5 h-5 text-orange-600" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  };

  const getOverallStatus = () => {
    if (!status) return { color: 'gray', message: 'Vérification...' };
    
    if (!status.configured) {
      return { color: 'red', message: '❌ Non configuré' };
    }
    
    if (!status.connected) {
      return { color: 'red', message: '❌ Non connecté' };
    }
    
    if (!status.tablesExist) {
      return { color: 'orange', message: '⚠️ Tables manquantes' };
    }
    
    return { color: 'green', message: '✅ Connecté et opérationnel' };
  };

  const overallStatus = getOverallStatus();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <Database className="w-8 h-8" />
          <h1 className="text-2xl font-bold">État de la connexion Supabase</h1>
        </div>
        <p className="text-blue-100">
          Vérification de la configuration et de la connectivité
        </p>
      </div>

      {/* Overall Status */}
      <div className={`p-6 rounded-xl border ${
        overallStatus.color === 'green' ? 'bg-green-50 border-green-200' :
        overallStatus.color === 'orange' ? 'bg-orange-50 border-orange-200' :
        overallStatus.color === 'red' ? 'bg-red-50 border-red-200' :
        'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-bold ${
            overallStatus.color === 'green' ? 'text-green-800' :
            overallStatus.color === 'orange' ? 'text-orange-800' :
            overallStatus.color === 'red' ? 'text-red-800' :
            'text-gray-800'
          }`}>
            {overallStatus.message}
          </h2>
          <button
            onClick={checkConnection}
            disabled={isChecking}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
            <span>Vérifier</span>
          </button>
        </div>
      </div>

      {/* Detailed Status */}
      {status && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Variables d'environnement</span>
                {getStatusIcon(status.configured)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">URL Supabase</span>
                {getStatusIcon(!!status.details.url && status.details.url !== 'Non configuré')}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Clé anonyme</span>
                {getStatusIcon(status.details.hasAnonKey)}
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">URL configurée :</p>
              <p className="text-xs font-mono text-gray-800 break-all">{status.details.url}</p>
            </div>
          </div>

          {/* Connection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Connexion</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Connexion base</span>
                {getStatusIcon(status.connected)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Tables existantes</span>
                {getStatusIcon(status.tablesExist)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Utilisateur connecté</span>
                {getStatusIcon(status.authenticated)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">RLS activé</span>
                {getStatusIcon(status.rlsEnabled, !status.rlsEnabled)}
              </div>
            </div>

            {/* Data Stats */}
            {status.connected && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Utilisateurs :</p>
                    <p className="font-medium text-gray-900">{status.details.userCount || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Projets :</p>
                    <p className="font-medium text-gray-900">{status.details.projectCount || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Details */}
      {status?.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Erreur de connexion</h3>
              <p className="text-red-800 mb-3">{status.error}</p>
              
              {/* Troubleshooting */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Solutions possibles :</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Vérifiez que les variables VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY sont définies</li>
                  <li>• Assurez-vous que l'URL Supabase est correcte</li>
                  <li>• Vérifiez que les migrations ont été appliquées</li>
                  <li>• Contrôlez les politiques RLS dans Supabase Dashboard</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-4">🔧 Guide de configuration</h3>
        
        <div className="space-y-4 text-sm text-blue-800">
          <div>
            <p className="font-medium">1. Variables d'environnement (.env) :</p>
            <div className="mt-2 p-3 bg-white rounded border font-mono text-xs">
              VITE_SUPABASE_URL=https://votre-projet.supabase.co<br/>
              VITE_SUPABASE_ANON_KEY=votre-cle-anonyme
            </div>
          </div>
          
          <div>
            <p className="font-medium">2. Configuration Supabase Dashboard :</p>
            <ul className="mt-2 space-y-1">
              <li>• Site URL: https://cressguadeloupe.online</li>
              <li>• Redirect URLs: https://cressguadeloupe.online/auth/callback</li>
              <li>• Disable email confirmations (pour les tests)</li>
            </ul>
          </div>
          
          <div>
            <p className="font-medium">3. Appliquer les migrations :</p>
            <p className="mt-1">Utilisez le SQL Editor de Supabase pour exécuter les fichiers de migration</p>
          </div>
        </div>
      </div>

      {/* Current Environment */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Environnement actuel</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Mode :</p>
            <p className="font-medium text-gray-900">{import.meta.env.DEV ? 'Développement' : 'Production'}</p>
          </div>
          <div>
            <p className="text-gray-600">URL actuelle :</p>
            <p className="font-medium text-gray-900 break-all">{window.location.origin}</p>
          </div>
          <div>
            <p className="text-gray-600">Dernière vérification :</p>
            <p className="font-medium text-gray-900">{status?.details.lastCheck || 'Jamais'}</p>
          </div>
          <div>
            <p className="text-gray-600">Navigateur :</p>
            <p className="font-medium text-gray-900">{navigator.userAgent.split(' ')[0]}</p>
          </div>
        </div>
      </div>
    </div>
  );
};